import { giphyWhite } from '@giphy/colors'
import { useEffect, useRef, useState } from 'react'
import { useWindowScroll, useWindowSize } from 'react-use'
import Portal from 'shared/components/portal'
import { SourceEditorCaption } from 'site/creation-tools/components/source-editor/types'
import styled from 'styled-components'
import Menu from './menu'

enum QuickActionButtonPosition {
    TOO_MUCH_RIGHT,
    TOO_MUCH_LEFT,
    NORMAL,
}

const RIGHT_MAXIMUM = 330

export const QuickActionsButton = styled.div<{ position?: QuickActionButtonPosition }>`
    position: absolute;
    cursor: pointer;
    transform: rotate(90deg);
    color: ${giphyWhite};

    ${({ position }) => {
        switch (position) {
            case QuickActionButtonPosition.TOO_MUCH_RIGHT:
                return 'left: -25px;'
            default:
                return 'right: -20px;'
        }
    }}

    i {
        font-size: 18px;
    }
`

// This shadow component exists to have a "shadow" of the original component
// and check if its visible of not to then change to the other side
export const QuickActionsShadowButton = styled.div<{ position?: QuickActionButtonPosition }>`
    position: absolute;
    right: -20px;
`

type QuickActionsProps = {
    onOpenMenu?: () => void
    onCloseMenu?: () => void
    caption: SourceEditorCaption
}

const QuickActions = (props: QuickActionsProps) => {
    const { onOpenMenu, onCloseMenu, caption } = props
    const [menuTop, setMenuTop] = useState<number>()
    const [menuLeft, setMenuLeft] = useState<number>()
    const [showMenu, setShowMenu] = useState<boolean>(false)
    const quickActionsShadowButtonRef = useRef<HTMLDivElement>(null)
    const quickActionsButtonRef = useRef<HTMLDivElement>(null)
    const { x, y } = useWindowScroll()
    const { width, height } = useWindowSize()

    const calculateMenuTopLeft = () => {
        const clientRect = quickActionsButtonRef?.current?.getBoundingClientRect()

        if (clientRect) {
            setMenuLeft(clientRect.left + 38)
            setMenuTop(clientRect.top + window.pageYOffset - 20)
        }
    }

    const position = () => {
        const quickActionButtonElement = quickActionsShadowButtonRef.current

        if (quickActionButtonElement) {
            const position = quickActionButtonElement.getBoundingClientRect()

            const distanceFromMiddle = position.x - window.innerWidth / 2

            if (distanceFromMiddle > RIGHT_MAXIMUM) {
                return QuickActionButtonPosition.TOO_MUCH_RIGHT
            }

            return QuickActionButtonPosition.NORMAL
        }
    }

    useEffect(() => {
        calculateMenuTopLeft()

        if (showMenu) {
            onOpenMenu?.()
        } else {
            onCloseMenu?.()
        }
    }, [quickActionsButtonRef, x, y, showMenu, width, height])

    return (
        <>
            <QuickActionsButton position={position()} ref={quickActionsButtonRef}>
                <i className="ss-ellipsis" id="ellipsisButton" onClick={() => setShowMenu((prev) => !prev)} />
            </QuickActionsButton>
            <QuickActionsShadowButton ref={quickActionsShadowButtonRef} />
            {showMenu && (
                <Portal>
                    <Menu top={menuTop} left={menuLeft} caption={caption} onClickOutside={() => setShowMenu(false)} />
                </Portal>
            )}
        </>
    )
}

export default QuickActions
